import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ExternalLink, Pencil, Plus, Search, Trash2 } from "lucide-react";
import {
  deleteSource,
  listPageSourcesAdmin,
  listSourcesAdmin,
  setPageSourceLinks,
  toggleSourcePublished,
  upsertPageSource,
} from "@/lib/admin-sources.functions";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { SourceInteractionLinksPanel } from "@/components/admin/SourceInteractionLinksPanel";

export const Route = createFileRoute("/_authenticated/_admin/admin/sources/")({
  component: AdminSourcesPage,
});

const KIND_LABELS: Record<string, string> = {
  leitlinie: "Leitlinie",
  behoerde: "Behörde",
  fachinfo: "Fachinformation",
  monitoring: "Monitoring",
  sekundaerliteratur: "Sekundärliteratur",
  primaerliteratur: "Primärliteratur",
};

const cardStyle: React.CSSProperties = {
  background: "var(--bg-surface)",
  border: "1px solid var(--border-purple)",
  borderRadius: "var(--radius-sm)",
  padding: "1.25rem",
  display: "flex",
  flexDirection: "column",
  gap: "0.75rem",
};

const inputStyle: React.CSSProperties = {
  background: "rgba(255,255,255,0.04)",
  border: "1px solid var(--border-purple)",
  borderRadius: 6,
  padding: "0.4rem 0.6rem",
  color: "var(--text-primary)",
  fontSize: "0.85rem",
};

type SourceRow = {
  id: string;
  key: string;
  title: string;
  publisher: string | null;
  year: string | null;
  kind: string;
  url: string | null;
  published: boolean;
  sort_order: number;
};

type PageRow = {
  id: string;
  path: string;
  intro_de: string | null;
  intro_en: string | null;
  open_claims_de: string[] | null;
  open_claims_en: string[] | null;
};

function AdminSourcesPage() {
  const qc = useQueryClient();
  const fetchSources = useServerFn(listSourcesAdmin);
  const fetchPages = useServerFn(listPageSourcesAdmin);
  const doDelete = useServerFn(deleteSource);
  const doToggle = useServerFn(toggleSourcePublished);
  const doSetLinks = useServerFn(setPageSourceLinks);
  const doSavePage = useServerFn(upsertPageSource);

  const [q, setQ] = useState("");
  const [kindFilter, setKindFilter] = useState("");
  const [target, setTarget] = useState<SourceRow | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [activePath, setActivePath] = useState<string | null>(null);
  const [savingPage, setSavingPage] = useState(false);
  const [pageDraft, setPageDraft] = useState<{ intro_de: string; intro_en: string; open_de: string; open_en: string } | null>(null);

  const { data: sources } = useQuery({ queryKey: ["admin-sources", "list"], queryFn: () => fetchSources() });
  const { data: pageData } = useQuery({ queryKey: ["admin-sources", "pages"], queryFn: () => fetchPages() });

  const rows = (sources ?? []) as SourceRow[];
  const pages = (pageData?.pages ?? []) as PageRow[];
  const links = (pageData?.links ?? []) as { page_source_id: string; source_id: string; sort_order: number }[];

  const filtered = useMemo(
    () =>
      rows.filter(
        (s) =>
          (kindFilter === "" || s.kind === kindFilter) &&
          (q.trim() === "" ||
            `${s.title} ${s.key} ${s.publisher ?? ""}`.toLowerCase().includes(q.trim().toLowerCase())),
      ),
    [rows, q, kindFilter],
  );

  const activePage = pages.find((p) => p.path === activePath) ?? null;
  const activeLinkIds = activePage
    ? links.filter((l) => l.page_source_id === activePage.id).map((l) => l.source_id)
    : [];

  function selectPage(path: string) {
    setActivePath(path);
    const p = pages.find((x) => x.path === path);
    setPageDraft({
      intro_de: p?.intro_de ?? "",
      intro_en: p?.intro_en ?? "",
      open_de: (p?.open_claims_de ?? []).join("\n"),
      open_en: (p?.open_claims_en ?? []).join("\n"),
    });
  }

  async function toggleLink(sourceId: string) {
    if (!activePage) return;
    const next = activeLinkIds.includes(sourceId)
      ? activeLinkIds.filter((id) => id !== sourceId)
      : [...activeLinkIds, sourceId];
    try {
      await doSetLinks({ data: { pageSourceId: activePage.id, sourceIds: next } });
      await qc.invalidateQueries({ queryKey: ["admin-sources", "pages"] });
    } catch (e) {
      toast.error("Zuordnung fehlgeschlagen", { description: e instanceof Error ? e.message : String(e) });
    }
  }

  async function savePage() {
    if (!activePage || !pageDraft) return;
    setSavingPage(true);
    try {
      await doSavePage({
        data: {
          path: activePage.path,
          intro_de: pageDraft.intro_de,
          intro_en: pageDraft.intro_en,
          open_claims_de: pageDraft.open_de.split("\n").map((s) => s.trim()).filter(Boolean),
          open_claims_en: pageDraft.open_en.split("\n").map((s) => s.trim()).filter(Boolean),
        },
      });
      toast.success("Seitentext gespeichert");
      await qc.invalidateQueries({ queryKey: ["admin-sources", "pages"] });
    } catch (e) {
      toast.error("Speichern fehlgeschlagen", { description: e instanceof Error ? e.message : String(e) });
    } finally {
      setSavingPage(false);
    }
  }

  async function confirmDelete() {
    if (!target) return;
    setDeleting(true);
    try {
      await doDelete({ data: { id: target.id } });
      toast.success(`Quelle "${target.title}" gelöscht`);
      await qc.invalidateQueries({ queryKey: ["admin-sources"] });
      setTarget(null);
    } catch (e) {
      toast.error("Löschen fehlgeschlagen", { description: e instanceof Error ? e.message : String(e) });
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div style={{ maxWidth: 1100, margin: "2rem auto", padding: "0 1.5rem", display: "flex", flexDirection: "column", gap: "1.25rem" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "1rem", flexWrap: "wrap" }}>
        <h1 style={{ color: "var(--accent-cyan)", margin: 0 }}>Quellen</h1>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <Link to="/admin" style={{ color: "var(--text-muted)", fontSize: "0.85rem" }}>Zurück zum Admin</Link>
          <Link to="/admin/sources/new" style={{ display: "inline-flex", alignItems: "center", gap: 4, color: "var(--accent-cyan)", textDecoration: "none", fontSize: "0.9rem" }}>
            <Plus size={15} /> Neue Quelle
          </Link>
        </div>
      </div>

      <div style={cardStyle}>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <label style={{ display: "flex", alignItems: "center", gap: 6, flex: 1, minWidth: 200 }}>
            <Search size={14} aria-hidden="true" style={{ color: "var(--text-muted)" }} />
            <span className="sr-only">Quellen durchsuchen</span>
            <input style={{ ...inputStyle, flex: 1 }} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Suchen" />
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span className="sr-only">Nach Art filtern</span>
            <select style={inputStyle} value={kindFilter} onChange={(e) => setKindFilter(e.target.value)}>
              <option value="">Alle Arten</option>
              {Object.entries(KIND_LABELS).map(([k, label]) => (
                <option key={k} value={k}>{label}</option>
              ))}
            </select>
          </label>
        </div>

        {filtered.length === 0 ? (
          <p style={{ color: "var(--text-muted)", fontSize: "0.85rem", margin: 0 }}>Keine Quellen gefunden.</p>
        ) : (
          <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 6 }}>
            {filtered.map((s) => (
              <li key={s.id} style={{ display: "flex", gap: 10, alignItems: "center", padding: "0.55rem 0.7rem", background: "rgba(255,255,255,0.03)", borderRadius: 6, flexWrap: "wrap" }}>
                <span style={{ flex: 1, minWidth: 220, color: "var(--text-primary)", fontSize: "0.9rem" }}>
                  {s.title}
                  <span style={{ display: "block", color: "var(--text-muted)", fontSize: "0.75rem" }}>
                    {KIND_LABELS[s.kind] ?? s.kind} · {s.publisher || "ohne Herausgeber"} · {s.year || "ohne Jahr"}
                  </span>
                </span>
                {s.url && (
                  <a href={s.url} target="_blank" rel="noopener noreferrer" style={{ color: "var(--accent-cyan)", fontSize: "0.78rem", display: "inline-flex", alignItems: "center", gap: 4 }}>
                    <ExternalLink size={12} aria-hidden="true" /> Link
                  </a>
                )}
                <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: "0.78rem", color: "var(--text-muted)" }}>
                  <input
                    type="checkbox"
                    checked={s.published}
                    onChange={async (e) => {
                      try {
                        await doToggle({ data: { id: s.id, published: e.target.checked } });
                        await qc.invalidateQueries({ queryKey: ["admin-sources"] });
                      } catch (err) {
                        toast.error("Änderung fehlgeschlagen", { description: err instanceof Error ? err.message : String(err) });
                      }
                    }}
                  />
                  Veröffentlicht
                </label>
                <Link to="/admin/sources/$id/edit" params={{ id: s.id }} style={{ color: "var(--accent-cyan)", fontSize: "0.78rem", display: "inline-flex", alignItems: "center", gap: 4 }}>
                  <Pencil size={12} aria-hidden="true" /> Bearbeiten
                </Link>
                <button
                  type="button"
                  onClick={() => setTarget(s)}
                  style={{ background: "transparent", border: "none", color: "#fca5a5", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4, fontSize: "0.78rem" }}
                >
                  <Trash2 size={12} aria-hidden="true" /> Löschen
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div style={cardStyle}>
        <h2 style={{ margin: 0, fontSize: "1.05rem", color: "var(--text-primary)" }}>Seitenzuordnung</h2>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {pages.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => selectPage(p.path)}
              style={{
                padding: "0.4rem 0.8rem",
                borderRadius: 999,
                border: "1px solid var(--border-purple)",
                background: activePath === p.path ? "var(--accent-bg, rgba(34,211,238,0.14))" : "transparent",
                color: activePath === p.path ? "var(--accent-cyan)" : "var(--text-muted)",
                cursor: "pointer",
                fontSize: "0.82rem",
              }}
            >
              {p.path}
            </button>
          ))}
        </div>

        {!activePage || !pageDraft ? (
          <p style={{ color: "var(--text-muted)", fontSize: "0.85rem", margin: 0 }}>
            Wähle eine Seite, um Einleitung, offene Punkte und die zugeordneten Quellen zu bearbeiten.
          </p>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "0.8rem" }}>
            <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: "0.78rem", color: "var(--text-muted)" }}>
              Einleitung (Deutsch)
              <textarea style={{ ...inputStyle, minHeight: 70 }} value={pageDraft.intro_de} onChange={(e) => setPageDraft({ ...pageDraft, intro_de: e.target.value })} />
            </label>
            <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: "0.78rem", color: "var(--text-muted)" }}>
              Einleitung (Englisch)
              <textarea style={{ ...inputStyle, minHeight: 70 }} value={pageDraft.intro_en} onChange={(e) => setPageDraft({ ...pageDraft, intro_en: e.target.value })} />
            </label>
            <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: "0.78rem", color: "var(--text-muted)" }}>
              Offene Punkte (Deutsch, eine Zeile pro Punkt)
              <textarea style={{ ...inputStyle, minHeight: 100 }} value={pageDraft.open_de} onChange={(e) => setPageDraft({ ...pageDraft, open_de: e.target.value })} />
            </label>
            <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: "0.78rem", color: "var(--text-muted)" }}>
              Offene Punkte (Englisch, eine Zeile pro Punkt)
              <textarea style={{ ...inputStyle, minHeight: 100 }} value={pageDraft.open_en} onChange={(e) => setPageDraft({ ...pageDraft, open_en: e.target.value })} />
            </label>
            <div>
              <button
                type="button"
                onClick={savePage}
                disabled={savingPage}
                style={{ background: "var(--accent-cyan)", color: "#001018", border: "none", borderRadius: 6, padding: "0.5rem 0.9rem", fontWeight: 600, cursor: savingPage ? "wait" : "pointer" }}
              >
                {savingPage ? "Speichere…" : "Seitentext speichern"}
              </button>
            </div>

            <fieldset style={{ border: "1px solid var(--border-purple)", borderRadius: 8, padding: "0.8rem" }}>
              <legend style={{ fontSize: "0.78rem", color: "var(--text-muted)", padding: "0 0.4rem" }}>
                Quellen dieser Seite
              </legend>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {rows.map((s) => (
                  <label key={s.id} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: "0.84rem", color: "var(--text-primary)" }}>
                    <input type="checkbox" checked={activeLinkIds.includes(s.id)} onChange={() => toggleLink(s.id)} />
                    <span>{s.title}</span>
                  </label>
                ))}
              </div>
            </fieldset>
          </div>
        )}
      </div>

      <div style={cardStyle}>
        <SourceInteractionLinksPanel sources={rows} />
      </div>


      <AlertDialog open={target !== null} onOpenChange={(o) => !o && !deleting && setTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Quelle wirklich löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              „{target?.title}" wird gelöscht. Alle Zuordnungen zu Seiten und Kombinationen gehen dabei verloren.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} disabled={deleting}>
              {deleting ? "Lösche…" : "Löschen"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
